'use client';

import React, { useEffect, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { useInView } from 'react-intersection-observer';
import { VideoPlayerProps } from './types';
import { useVideoPlayerState, useVideoPlayerChildren } from './hooks';
import { getMergedClassNames } from './utils';
import { VIDEO_PLAYER_CONSTANTS } from './constants';

const VideoPlayer: React.FC<VideoPlayerProps> = React.memo(
  ({
    video,
    classNames = {},
    autoPlay = false,
    muted = false,
    rounded = false,
    loop = false,
    hideNativeControls = false,
    playInBackground = false,
    lazyLoad = false,
    lazyLoadRootMargin = VIDEO_PLAYER_CONSTANTS.DEFAULT_LAZY_LOAD_ROOT_MARGIN,
    testId = VIDEO_PLAYER_CONSTANTS.DEFAULT_TEST_ID,
    onReady = () => {},
    onStart = () => {},
    onPlay = () => {},
    onPause = () => {},
    onEnded = () => {},
    children,
    ...rest
  }) => {
    // Memoized computed values
    const mergedClassNames = useMemo(() => getMergedClassNames(classNames, rounded), [classNames, rounded]);
    const shouldObserveViewport = lazyLoad;
    const { ref: inViewRef, inView } = useInView({
      triggerOnce: true,
      threshold: 0,
      rootMargin: lazyLoadRootMargin,
      skip: !shouldObserveViewport,
    });

    const {
      setPlayerRef,
      playerId,
      requestPlayerMount,
      // Optimized computed values and state objects
      videoPlayerState,
      playerConfig,
      wrapperProps,
      viewportProps,
      shouldMountPlayer,
    } = useVideoPlayerState({
      video,
      autoPlay,
      playInBackground,
      lazyLoad,
      onReady,
      onStart,
      onPlay,
      onPause,
      onEnded,
      testId,
      muted,
      loop,
      hideNativeControls,
      mergedClassNames,
    });

    useEffect(() => {
      if (inView && shouldObserveViewport) {
        requestPlayerMount();
      }
    }, [requestPlayerMount, inView, shouldObserveViewport]);

    // Custom hook for child rendering - memoized to prevent unnecessary re-renders
    const renderChildren = useVideoPlayerChildren(children, videoPlayerState);

    return (
      <div {...wrapperProps} data-video-player-id={playerId} {...rest}>
        {/* Video Player Viewport */}
        <div {...viewportProps} ref={inViewRef}>
          {playerConfig && shouldMountPlayer && (
            <>
              <ReactPlayer ref={setPlayerRef} {...playerConfig} playsInline />
            </>
          )}

          {/* Render children with appropriate videoPlayerState */}
          {renderChildren()}
        </div>
      </div>
    );
  },
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
